/**
 * Parent-side helper for the full HitL Agent UI iframe postMessage bridge.
 * @see hitl/docs/hitl-iframe-event-bridge.md
 */

/**
 * @param {HTMLIFrameElement} iframe
 * @param {{ targetOrigin?: string, onEvent?: (payload: { message: string, params?: unknown }) => void }} [options]
 */
export function createHitlIframeBridge(iframe, { targetOrigin = '*', onEvent } = {}) {
  const pending = new Map();

  const onMessage = (event) => {
    const { message, params } = event.data ?? {};
    if (!message?.startsWith('HITL::')) return;

    const callbackId = params?.callbackId;
    if (callbackId && pending.has(callbackId)) {
      pending.get(callbackId)(params);
      pending.delete(callbackId);
    }

    onEvent?.({ message, params });
  };

  window.addEventListener('message', onMessage);

  const send = (message, params = {}) => {
    iframe.contentWindow?.postMessage({ message, params }, targetOrigin);
  };

  const request = (message, params, callbackId) =>
    new Promise((resolve) => {
      pending.set(callbackId, resolve);
      send(message, { ...params, callbackId });
    });

  const ping = () => send('HITL::app-ping', {});

  const destroy = () => {
    window.removeEventListener('message', onMessage);
    pending.clear();
  };

  return { send, request, ping, destroy };
}
